import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import {
  AlertCircle,
  Building2,
  CheckCircle,
  CreditCard,
  Info,
  Upload,
  Wallet,
} from 'lucide-react';
import '../../../../../styles/components/auction-client-pay.scss';
import { toast } from 'react-toastify';
import { getUserDetails, mzadcomPayment } from '../services/mzadcomPaymentService';

export class MzadcomPayModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentMethod: '',
      walletBalance: 0,
      editableBalances: {},
      loadingProfile: true,

      // Offline payment fields
      bankName: props.user?.bank || '',
      receipt: '',
      receiptImage: null,

      // Online payment fields
      cardType: '',

      showPolicies: {
        offline: false,
        online: false,
      },
    };
  }

  async componentDidMount() {
    this.initializeBalances();
    await this.fetchUserDetails();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedAuctions !== this.props.selectedAuctions && this.props.selectedAuctions) {
      this.initializeBalances();
    }
    if (!prevProps.show && this.props.show) {
      this.fetchUserDetails();
      const updatedBalances = {};
      this.props.selectedAuctions.forEach((auction) => {
        updatedBalances[auction.id] = parseFloat(auction.balance_amount || 0);
      });
      this.setState({ editableBalances: updatedBalances });
    }
  }

  initializeBalances = () => {
    if (this.props.selectedAuctions) {
      const editableBalances = {};
      this.props.selectedAuctions.forEach((auction) => {
        // Map the correct property names from the API response
        editableBalances[auction.id] = parseFloat(auction.balance_amount || 0);
      });
      this.setState({ editableBalances });
    }
  };

  fetchUserDetails = async () => {
    this.setState({ loadingProfile: true });
    try {
      const response = await getUserDetails();

      if (response.success) {
        const { data } = response;
        this.setState({
          walletBalance: parseFloat(data.wallet_amount || 0),
        });
      }
    } catch (error) {
      console.error('Failed to fetch wallet balance', error);
    } finally {
      this.setState({ loadingProfile: false });
    }
  };

  handleBalanceChange = (auctionId, value) => {
    const auction = this.props.selectedAuctions.find((a) => a.id === auctionId);
    const maxBalance = parseFloat(auction.balance_amount || 0);
    const numValue = parseFloat(value) || 0;

    let newBalance = numValue;
    if (numValue > maxBalance) {
      newBalance = maxBalance;
      toast.warning(`Maximum payment amount for ${auction.title} is ${maxBalance.toFixed(2)}`);
    } else if (numValue < 0) {
      newBalance = 0;
      toast.error('Payment amount cannot be negative.');
    }

    this.setState((prevState) => ({
      editableBalances: {
        ...prevState.editableBalances,
        [auctionId]: newBalance,
      },
    }));
  };

  getTotalPaymentAmount = () => {
    return Object.values(this.state.editableBalances).reduce((total, amount) => total + amount, 0);
  };

  getCommission = () => {
    const { cardType } = this.state;
    const amount = this.getTotalPaymentAmount();

    if (cardType === 'debit') {
      return amount * 0.015;
    } else if (cardType === 'credit') {
      return amount * 0.02;
    }
    return 0;
  };

  getSubtotal = () => {
    const amount = this.getTotalPaymentAmount();
    const commission = this.getCommission();
    return amount + commission;
  };
  getPendingBalance = () => {
    const totalBalance = (this.props.selectedAuctions || []).reduce(
      (sum, auction) => sum + parseFloat(auction.balance_amount || 0),
      0
    );
    return totalBalance - this.getSubtotal();
  };
  canPayWithWallet = () => {
    const amount = this.getTotalPaymentAmount();
    return amount <= this.state.walletBalance;
  };

  canPayOnline = () => {
    const amount = this.getTotalPaymentAmount();
    return amount < 5000;
  };

  togglePolicy = (type) => {
    this.setState((prevState) => ({
      showPolicies: {
        ...prevState.showPolicies,
        [type]: !prevState.showPolicies[type],
      },
    }));
  };

  handlePayment = async () => {
    const { paymentMethod, editableBalances, bankName, receipt, receiptImage } = this.state;

    const { selectedAuctions } = this.props;
    const totalAmount = this.getTotalPaymentAmount();
    if (totalAmount <= 0) return;

    // Validate offline fields
    if (paymentMethod === 'offline') {
      if (!bankName || !receipt || !receiptImage) {
        toast.error('All offline payment fields are required.');
        return;
      }
      if (!/^[A-Za-z0-9]{4,}$/.test(receipt)) {
        toast.error('Receipt number must be at least 4 letters or numbers.');
        return;
      }
    }

    // Create FormData
    const formData = new FormData();
    let subtotal = 0;

    selectedAuctions.forEach((a, index) => {
      const amount = editableBalances[a.id] || 0;
      subtotal += amount;

      formData.append(`auctions[${index}][id]`, a.id);
      formData.append(`auctions[${index}][amount]`, editableBalances[a.id] || 0);
    });
    formData.append('subtotal', subtotal);
    formData.append('paymentMethod', paymentMethod);

    if (paymentMethod === 'offline') {
      formData.append('bankName', bankName);
      formData.append('receiptNumber', receipt);
      formData.append('receiptImage', receiptImage); // File object
    }

    try {
      this.setState({ submitting: true });
      const response = await mzadcomPayment(formData, true);
      if (response.success) {
        toast.success(response.message || 'Payment successful');
        this.props.onPaymentComplete?.();
        this.handleCancel();
      } else {
        toast.error(response.message || 'Payment failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleCancel = () => {
    // Reset form state
    this.setState({
      walletBalance: 0,
      editableBalances: {},
      paymentMethod: '',
      bankName: '',
      receipt: '',
      receiptImage: null,
      cardType: '',
      showPolicies: { offline: false, online: false },
    });

    this.props.onClose();
  };

  handleFileUpload = (event) => {
    const file = event.target.files[0];
    this.setState({ receiptImage: file });
  };

  render() {
    const { show, selectedAuctions = [] } = this.props;
    const {
      paymentMethod,
      walletBalance,
      editableBalances,
      bankName,
      receipt,
      receiptImage,
      cardType,
      showPolicies,
      loadingProfile,
    } = this.state;

    const totalPaymentAmount = this.getTotalPaymentAmount();

    return (
      <CustomModal
        isOpen={show}
        header={`Payment for ${selectedAuctions.length} Selected Auction`}
        onClose={this.handleCancel}
      >
        <div className="payment-container">
          {loadingProfile ? (
            <div className="skeleton-loader">
              <div className="skeleton skeleton-header"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
            </div>
          ) : (
            <div className="payment-content">
              <div className="auction-summary">
                <h3>Payment Summary</h3>
                {selectedAuctions.map((auction) => (
                  <div key={auction.id} className="auction-item">
                    <div className="auction-info">
                      <div className="auction-id">MZAD {auction.id}</div>
                      <div className="auction-name">{auction.title}</div>
                      <div className="amounts">
                        <span>Total : {parseFloat(auction.total_amount || 0).toFixed(2)}</span>
                        <span>Paid : {parseFloat(auction.total_paid || 0).toFixed(2)}</span>
                        <span>Balance : {parseFloat(auction.balance_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="payment-amount-edit">
                      <label>Payment Amount</label>
                      <div
                        className={`amount-input-group {
                        editableBalances[auction.id] > parseFloat(auction.balance_amount || 0)
                          ? 'error'
                          : editableBalances[auction.id] > 0
                          ? 'valid'
                          : ''
                      }`}
                      >
                        <input
                          type="number"
                          value={editableBalances[auction.id] || ''}
                          onChange={(e) => this.handleBalanceChange(auction.id, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          onBlur={(e) => {
                            // Remove leading zeros on blur
                            const val = parseFloat(e.target.value) || 0;
                            this.handleBalanceChange(auction.id, val.toString());
                          }}
                          max={parseFloat(auction.balance_amount || 0)}
                          min="0"
                          step="0.01"
                          placeholder={`Max: ${parseFloat(auction.balance_amount || 0).toFixed(2)}`}
                        />
                      </div>
                      {editableBalances[auction.id] > parseFloat(auction.balance_amount || 0) && (
                        <div className="error-message">
                          <AlertCircle className="error-icon" />
                          Exceeds limit of {parseFloat(auction.balance_amount || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="summary-row total">
                  <span>Total Payment Amount </span>
                  <span>{totalPaymentAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="payment-methods">
                <h3>Select Payment Method</h3>

                {/* Offline Payment */}
                <div className={`payment-option {paymentMethod === 'offline' ? 'selected' : ''}`}>
                  <div
                    className="option-header"
                    onClick={() => this.setState({ paymentMethod: 'offline' })}
                  >
                    <Building2 className="option-icon" />
                    <span>Offline Payment</span>
                    <button
                      className="policy-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.togglePolicy('offline');
                      }}
                    >
                      <Info className="icon" />
                    </button>
                  </div>

                  {showPolicies.offline && (
                    <div className="policy-section">
                      <h4>Wallet Amount Deposit and Refund Policy</h4>
                      <ul>
                        <li>Deposits are processed within 24 hours</li>
                        <li>Refunds take 3-5 business days</li>
                        <li>Minimum deposit amount: 50</li>
                        <li>Transaction fees may apply</li>
                      </ul>
                    </div>
                  )}

                  {paymentMethod === 'offline' && (
                    <div className="payment-form">
                      <div className="form-group">
                        <label>Bank Name *</label>
                        <input
                          type="text"
                          value={bankName}
                          onChange={(e) => this.setState({ bankName: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Receipt *</label>
                        <input
                          type="text"
                          value={receipt}
                          onChange={(e) => this.setState({ receipt: e.target.value })}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Receipt Image **</label>
                        <div className="file-upload">
                          <input
                            type="file"
                            onChange={this.handleFileUpload}
                            accept="image/*,application/pdf"
                            className="file-input"
                            id="receipt-upload"
                          />
                          <label htmlFor="receipt-upload" className="file-label">
                            <Upload className="icon" />
                            {receiptImage ? receiptImage.name : 'Choose Image'}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Online Payment */}
                <div className={`payment-option {paymentMethod === 'online' ? 'selected' : ''}`}>
                  <div
                    className="option-header"
                    onClick={() => this.setState({ paymentMethod: 'online' })}
                  >
                    <CreditCard className="option-icon" />
                    <span>Online Payment</span>
                    {!this.canPayOnline() && (
                      <span className="restriction">Only amounts below 5000</span>
                    )}
                    <button
                      className="policy-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.togglePolicy('online');
                      }}
                    >
                      <Info className="icon" />
                    </button>
                  </div>

                  {showPolicies.online && (
                    <div className="policy-section">
                      <h4>Online Deposit and Refund Policy</h4>
                      <ul>
                        <li>Instant payment processing</li>
                        <li>Refunds processed within 5-7 business days</li>
                        <li>Secure SSL encryption</li>
                        <li>Commission fees apply based on card type</li>
                      </ul>
                    </div>
                  )}

                  {/* {paymentMethod === 'online' && this.canPayOnline() && (
                    <div className="payment-form">
                      <div className="form-group">
                        <label>Card Type</label>
                        <select
                          value={cardType}
                          onChange={(e) => this.setState({ cardType: e.target.value })}
                          className="form-select"
                          disabled
                        >
                          <option value="">Select Card Type</option>
                          <option value="debit">Debit Card (1.5% commission)</option>
                          <option value="credit">Credit Card (2% commission)</option>
                        </select>
                      </div>
                      {cardType && (
                        <div className="commission-info">
                          <span>Commission: {this.getCommission().toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {paymentMethod === 'online' && !this.canPayOnline() && (
                    <div className="restriction-message">
                      <AlertCircle className="icon" />
                      <span>Online payment only available for amounts below 5000</span>
                    </div>
                  )} */}
                </div>

                {/* Wallet Payment */}
                <div className={`payment-option {paymentMethod === 'wallet' ? 'selected' : ''}`}>
                  <div
                    className="option-header"
                    onClick={() => this.setState({ paymentMethod: 'wallet' })}
                  >
                    <Wallet className="option-icon" />
                    <span>Wallet Payment</span>
                    <span className="wallet-balance">Balance: {walletBalance}</span>
                  </div>

                  {paymentMethod === 'wallet' && (
                    <div className="wallet-info">
                      {this.canPayWithWallet() ? (
                        <div className="wallet-sufficient">
                          <CheckCircle className="icon" />
                          <span>Sufficient wallet balance</span>
                        </div>
                      ) : (
                        <div className="wallet-insufficient">
                          <AlertCircle className="icon" />
                          <span>
                            Insufficient wallet balance (Need: {totalPaymentAmount.toFixed(2)})
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {paymentMethod && (
                <div className="payment-summary">
                  <div className="summary-section">
                    <h4>Final Payment Summary</h4>
                    <div className="summary-row">
                      <span>Pending Balance</span>
                      <span>{this.getPendingBalance().toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Total Payment Amount</span>
                      <span>{totalPaymentAmount.toFixed(2)}</span>
                    </div>
                    {paymentMethod === 'online' && cardType && (
                      <div className="summary-row">
                        <span>Commission ({cardType === 'debit' ? '1.5%' : '2%'}):</span>
                        <span>{this.getCommission().toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Subtotal</span>
                      <span>{this.getSubtotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="action-buttons-new">
                    <button className="cancel-btn" onClick={this.handleCancel}>
                      Cancel
                    </button>
                    <button
                      className="pay-btn"
                      onClick={this.handlePayment}
                      disabled={
                        (paymentMethod === 'wallet' && !this.canPayWithWallet()) ||
                        (paymentMethod === 'online' && !this.canPayOnline()) ||
                        totalPaymentAmount === 0
                      }
                    >
                      {this.state.submitting ? (
                        <>
                          <span className="spinner"></span>
                          Processing Payment...
                        </>
                      ) : (
                        <>Pay {this.getSubtotal().toFixed(2)} Now</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>
    );
  }
}
